import { getEncounterTimelineData } from 'app/frontend/base/services/raidPlanData'
import {
  PlanAbilityDefaults,
  resolveCastReminderText,
  resolveCastRgb,
  resolveCastTtsMessage,
} from '../../TeamPlans/components/planAbilityDefaults'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import i18n from 'i18next'
import {
  getAssignmentScopes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentType,
  getEffectiveAssignmentViewMode,
  getSoakPrefix,
  isGeneralAssignmentType,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  resolvePhaseTiming,
  resolvePhaseIndexAtTime,
} from '../../TeamPlans/utils/phaseTiming'
import {
  formatNotePhaseNumber,
  toNotePhaseNumber,
} from '../../TeamPlans/utils/notePhaseNumbers'
import {
  EventActivityLinkedPlanData,
  EventActivityLinkedRaidPlanData,
} from '../hooks/useEventActivityLinkedPlanData'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'

type TimedExportLine = {
  at: number
  line: string
}

export type AssignmentSummaryScope = {
  id?: string
  label: string
  groups: Array<{ name: string; tokens: string[] }>
}

export type AssignmentSummary = {
  title: string
  subtitle?: string
  assignmentGroups: number
  scopes: AssignmentSummaryScope[]
}

export const formatAssignmentSummaryLines = (
  summary: AssignmentSummary,
): string[] => {
  const header = summary.subtitle
    ? `${summary.title} (${summary.subtitle})`
    : summary.title
  const lines = [header]
  summary.scopes.forEach((scope) => {
    const groups = scope.groups.filter((group) => group.tokens.length > 0)
    if (!groups.length) return
    if (summary.assignmentGroups <= 1) {
      lines.push(`${scope.label}: ${groups[0].tokens.join(', ')}`)
      return
    }
    const prefix = `${scope.label}: `
    groups.forEach((group, index) => {
      const body = `${group.name} - ${group.tokens.join(', ')}`
      lines.push(
        index === 0
          ? `${prefix}${body}`
          : `${' '.repeat(prefix.length)}${body}`,
      )
    })
  })
  return lines
}

const normalizeRealmChatName = (realmName: string) =>
  realmName.replaceAll(' ', '').replaceAll('-', '')

const formatTimeValue = (value: number) =>
  Math.max(Math.floor(value), 0).toString()

const normalizeRoleToken = (role: string) => {
  if (role === 'heal') return 'healer'
  return role
}

const toEntityToken = (entity: ResolvedAssignmentEntity) => {
  if (entity.kind === 'ignored') return ''
  if (entity.kind === 'participant') return entity.participant.name
  if (entity.sourceEntity.kind === 'everyone') return 'everyone'
  if (entity.sourceEntity.kind === 'group')
    return `group${entity.sourceEntity.group}`
  if (entity.sourceEntity.kind === 'role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'collection') {
    if (entity.sourceEntity.collection === 'everyone') return 'everyone'
    if (entity.sourceEntity.collection === 'healers') return 'healer'
    if (entity.sourceEntity.collection === 'tanks') return 'tank'
    return entity.sourceEntity.collection
  }
  if (entity.sourceEntity.kind === 'by_role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'member') return entity.sourceEntity.label
  return ''
}

const getBossTimelineEntries = ({
  maxTime,
  phaseStarts,
  phaseEnds,
  phaseDurations,
  abilities,
}: {
  maxTime: number
  phaseStarts: number[]
  phaseEnds: number[]
  phaseDurations: number[]
  abilities: Record<
    string,
    {
      spell_id: number
      entries: Array<{ time: number; duration: number; phaseIndex: number }>
    }
  >
}) => {
  return Object.values(abilities).flatMap((ability) =>
    ability.entries.flatMap((entry) => {
      const phaseStart = phaseStarts[entry.phaseIndex] ?? 0
      const phaseEnd = phaseEnds[entry.phaseIndex] ?? maxTime
      const phaseDuration = phaseDurations[entry.phaseIndex]
      if (phaseDuration === undefined) return []
      if (entry.time > phaseDuration || entry.time < 0) return []
      const entryTime = Math.floor(phaseStart + entry.time)
      if (entryTime < phaseStart || entryTime >= phaseEnd) return []
      return [
        {
          spellId: ability.spell_id,
          time: entryTime,
        },
      ]
    }),
  )
}

const getNearestBossSpellId = (
  castAt: number,
  bossEntries: Array<{ spellId: number; time: number }>,
) => {
  const candidates = bossEntries
    .map((entry) => ({
      spellId: entry.spellId,
      delta: Math.abs(entry.time - castAt),
      time: entry.time,
    }))
    .filter((entry) => entry.delta <= 3)
    .sort((left, right) => {
      if (left.delta !== right.delta) return left.delta - right.delta
      if (left.time !== right.time) return left.time - right.time
      return left.spellId - right.spellId
    })
  return candidates[0]?.spellId
}

const toAssignmentEntityTokens = (entities: ResolvedAssignmentEntity[]) =>
  entities
    .filter((entity) => entity.kind !== 'ignored' && !entity.issue)
    .map(toEntityToken)
    .filter((token) => token.length > 0)

const toCastTargetSuffix = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  if (!resolvedTargets.length) return ''
  const tokens = toAssignmentEntityTokens(resolvedTargets)
  if (!tokens.length) return ''
  return ` on ${tokens.join(', ')}`
}

const toCastTargetCharacterNames = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  const seenNames = new Set<string>()
  return resolvedTargets.reduce<string[]>((acc, entity) => {
    if (entity.kind !== 'participant' || entity.issue) return acc
    const name = entity.participant.name.trim()
    if (!name.length || seenNames.has(name)) return acc
    seenNames.add(name)
    acc.push(name)
    return acc
  }, [])
}

const appendTextProperty = (parts: string[], text?: string) => {
  const trimmed = text?.trim()
  if (trimmed) {
    parts.push(`text:${trimmed}`)
  }
}

const appendTtsProperty = (parts: string[], ttsMessage?: string) => {
  const trimmed = ttsMessage?.trim()
  if (trimmed) {
    parts.push(`TTS:${trimmed}`)
  }
}

const parseRgbString = (value?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const rgbMatch = trimmed.match(
    /^rgb\(\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*\)$/i,
  )
  if (!rgbMatch) return null
  const red = Number(rgbMatch[1])
  const green = Number(rgbMatch[2])
  const blue = Number(rgbMatch[3])
  if (
    !Number.isFinite(red) ||
    !Number.isFinite(green) ||
    !Number.isFinite(blue)
  ) {
    return null
  }
  const normalize = (channel: number, max: number) =>
    Number((Math.max(0, Math.min(channel, max)) / max).toFixed(4))
  return {
    red: normalize(red, 255),
    green: normalize(green, 255),
    blue: normalize(blue, 255),
    alpha: 1,
  }
}

const appendColorsProperty = (parts: string[], rgb?: string) => {
  const parsed = parseRgbString(rgb)
  if (!parsed) return
  parts.push(
    `colors:${parsed.red} ${parsed.green} ${parsed.blue} ${parsed.alpha}`,
  )
}

const appendGlowunitProperty = (parts: string[], characterNames: string[]) => {
  if (!characterNames.length) return
  parts.push(`glowunit:${characterNames.join(' ')}`)
}

const buildInviteList = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const selectedParticipantIds = new Set(
    (selectedParticipants ?? []).map(
      (participant) => participant.participantId,
    ),
  )
  const fallbackEntries = allEntries.filter((entry) => {
    if (unavailableParticipantStatuses.includes(entry.participant.status))
      return false
    return entry.selections[activity.id]?.selected === true
  })
  const baseEntries =
    selectedParticipantIds.size > 0
      ? allEntries.filter((entry) =>
          selectedParticipantIds.has(entry.participant.id),
        )
      : fallbackEntries

  return baseEntries
    .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
    .map((character) => ({
      id: character.id,
      name: character.name,
      value: `${character.name}-${normalizeRealmChatName(
        character.realm.name,
      )}`,
    }))
    .reduce<Array<{ id: number; name: string; value: string }>>((acc, item) => {
      if (acc.some((entry) => entry.id === item.id)) return acc
      acc.push(item)
      return acc
    }, [])
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => item.value)
}

export const buildEventActivityInviteListLine = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const inviteList = buildInviteList({
    activity,
    allEntries,
    selectedParticipants,
  })
  return `invitelist:${inviteList.join(' ')};`
}

const buildLinkedRaidPlanNoteParts = (
  raidPlanData: EventActivityLinkedRaidPlanData,
  planAbilityDefaults?: PlanAbilityDefaults,
): {
  timedLines: TimedExportLine[]
  summaryLines: string[]
} => {
  const linkedRaidPlan = raidPlanData.linkedRaidPlan
  const encounterTimeline =
    getEncounterTimelineData()[linkedRaidPlan.encounterId.toString()]
  const encounterDifficulty =
    encounterTimeline?.difficulties.find(
      (difficulty) => difficulty.difficulty === linkedRaidPlan.difficulty,
    ) ?? encounterTimeline?.difficulties[0]
  if (!encounterDifficulty) {
    return { timedLines: [], summaryLines: [] }
  }

  const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30
  const encounterMax = encounterDifficulty.maxTime ?? 0
  const maxTime =
    encounterMax > 0
      ? roundUpToThirtySeconds(Math.max(60, encounterMax))
      : roundUpToThirtySeconds(
          Math.max(
            60,
            encounterDifficulty.phases.reduce(
              (sum, phase) => sum + phase.duration,
              0,
            ),
            linkedRaidPlan.timeline.actors.reduce((max, actor) => {
              return actor.casts.reduce(
                (innerMax, cast) =>
                  cast.hidden ? innerMax : Math.max(innerMax, cast.at),
                max,
              )
            }, 0),
            (linkedRaidPlan.timeline.general?.casts ?? []).reduce(
              (max, cast) => (cast.hidden ? max : Math.max(max, cast.at)),
              0,
            ),
          ),
        )
  const { effectivePhaseDurations, phaseStarts, phaseEnds, phaseOrder } =
    resolvePhaseTiming({
      phases: encounterDifficulty.phases,
      persistedPhaseDurations: linkedRaidPlan.timeline.phase_durations,
      phaseOrder: linkedRaidPlan.timeline.phase_order,
    })
  const bossEntries = getBossTimelineEntries({
    maxTime,
    phaseStarts,
    phaseEnds,
    phaseDurations: effectivePhaseDurations,
    abilities: encounterDifficulty.abilities,
  })
  const notePhaseProperty = (phaseIndex: number) =>
    `ph:${formatNotePhaseNumber(
      toNotePhaseNumber(encounterDifficulty.phases, phaseIndex),
    )}`

  const actorCastLines: TimedExportLine[] = raidPlanData.resolvedActors
    .filter(
      (resolvedActor) =>
        !resolvedActor.issue &&
        resolvedActor.participant &&
        !resolvedActor.isIgnored,
    )
    .flatMap((resolvedActor) => {
      return resolvedActor.actor.casts
        .filter((cast) => !cast.hidden)
        .map((cast) => {
          const phaseIndex = resolvePhaseIndexAtTime(
            cast.at,
            phaseStarts,
            phaseEnds,
            phaseOrder,
          )
          const phaseStart = phaseStarts[phaseIndex] ?? 0
          const relativeTime = Math.max(cast.at - phaseStart, 0)
          const parts = [
            `time:${formatTimeValue(relativeTime)}`,
            notePhaseProperty(phaseIndex),
          ]
          const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
          if (bossSpellId) {
            parts.push(`bossSpell:${bossSpellId}`)
          }
          parts.push(
            `tag:${
              resolvedActor.participant?.name ?? resolvedActor.actor.label
            }`,
          )
          if (cast.spell_id !== null) {
            parts.push(`spellid:${cast.spell_id}`)
          }
          const shouldAddTargetsToReminderText =
            cast.add_targets_to_reminder_text !== false
          const reminderText = resolveCastReminderText({
            message: cast.message,
            spellId: cast.spell_id,
            defaults: planAbilityDefaults,
          })
          const castMessage = `${reminderText}${
            shouldAddTargetsToReminderText
              ? toCastTargetSuffix(
                  cast.id,
                  raidPlanData.castTargetResolverResult.resolvedByCastId,
                )
              : ''
          }`.trim()
          appendTextProperty(parts, castMessage)
          appendColorsProperty(
            parts,
            resolveCastRgb({
              rgb: cast.rgb,
              spellId: cast.spell_id,
              defaults: planAbilityDefaults,
            }),
          )
          appendTtsProperty(
            parts,
            resolveCastTtsMessage({
              ttsMessage: cast.tts_message,
              spellId: cast.spell_id,
              defaults: planAbilityDefaults,
            }),
          )
          if (cast.glow_targets_on_raid_frames !== false) {
            appendGlowunitProperty(
              parts,
              toCastTargetCharacterNames(
                cast.id,
                raidPlanData.castTargetResolverResult.resolvedByCastId,
              ),
            )
          }
          if (cast.spell_id === null && !castMessage.length) {
            return null
          }
          return {
            at: cast.at,
            line: `${parts.join(';')};`,
          }
        })
        .filter((entry): entry is TimedExportLine => entry !== null)
    })
  const generalCastLines: TimedExportLine[] = (
    linkedRaidPlan.timeline.general?.casts ?? []
  )
    .filter((cast) => !cast.hidden)
    .map((cast) => {
      const phaseIndex = resolvePhaseIndexAtTime(
        cast.at,
        phaseStarts,
        phaseEnds,
        phaseOrder,
      )
      const phaseStart = phaseStarts[phaseIndex] ?? 0
      const relativeTime = Math.max(cast.at - phaseStart, 0)
      const parts = [
        `time:${formatTimeValue(relativeTime)}`,
        notePhaseProperty(phaseIndex),
      ]
      const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
      if (bossSpellId) {
        parts.push(`bossSpell:${bossSpellId}`)
      }
      parts.push('tag:everyone')
      if (cast.spell_id !== null) {
        parts.push(`spellid:${cast.spell_id}`)
      }
      const shouldAddTargetsToReminderText =
        cast.add_targets_to_reminder_text !== false
      const reminderText = resolveCastReminderText({
        message: cast.message,
        spellId: cast.spell_id,
        defaults: planAbilityDefaults,
      })
      const castMessage = `${reminderText}${
        shouldAddTargetsToReminderText
          ? toCastTargetSuffix(
              cast.id,
              raidPlanData.castTargetResolverResult.resolvedByCastId,
            )
          : ''
      }`.trim()
      appendTextProperty(parts, castMessage)
      appendColorsProperty(
        parts,
        resolveCastRgb({
          rgb: cast.rgb,
          spellId: cast.spell_id,
          defaults: planAbilityDefaults,
        }),
      )
      appendTtsProperty(
        parts,
        resolveCastTtsMessage({
          ttsMessage: cast.tts_message,
          spellId: cast.spell_id,
          defaults: planAbilityDefaults,
        }),
      )
      if (cast.glow_targets_on_raid_frames !== false) {
        appendGlowunitProperty(
          parts,
          toCastTargetCharacterNames(
            cast.id,
            raidPlanData.castTargetResolverResult.resolvedByCastId,
          ),
        )
      }
      if (cast.spell_id === null && !castMessage.length) {
        return null
      }
      return {
        at: cast.at,
        line: `${parts.join(';')};`,
      }
    })
    .filter((entry): entry is TimedExportLine => entry !== null)

  const assignmentLines: TimedExportLine[] = []
  const summaryByAbility = new Map<string, AssignmentSummary>()

  raidPlanData.linkedPlanAssignments.forEach((normalizedEntry) => {
    const ability = normalizedEntry.ability
    const row = normalizedEntry.row
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const abilityName = ability.title?.trim() || row.name
    const defaultAssignmentLabel = `${getSoakPrefix(
      abilityName,
      ability.assignment_type,
      row.isCustom,
    )}${abilityName}`
    const customAbilityMessage = ability.message?.trim()
    const assignmentLabel = customAbilityMessage || defaultAssignmentLabel
    const isGeneralAssignment = isGeneralAssignmentType(
      getEffectiveAssignmentType(ability.assignment_type),
    )
    const viewMode = isGeneralAssignment
      ? 'all_together'
      : getEffectiveAssignmentViewMode(ability.view_mode, row.entries.length)
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopes(
      row.entries,
      viewMode,
      i18n.t,
      customGrouping,
      ability.assignment_type,
      row.isCustom,
    )
    const useBoxNoteSettings =
      viewMode !== 'all_together' || assignmentGroups > 1
    scopes.forEach((scope) => {
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )
      const scopeLabel = isGeneralAssignment ? assignmentLabel : scope.label
      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities =
          raidPlanData.resolverResult.resolvedByScopeBoxKey[key] ?? []
        const tokens = toAssignmentEntityTokens(resolvedEntities)
        if (!tokens.length) continue
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
        const boxMessage = useBoxNoteSettings ? box.message?.trim() : ''
        const boxTtsMessage = useBoxNoteSettings ? box.tts_message?.trim() : ''
        const lineText = boxMessage?.length ? boxMessage : assignmentLabel
        const lineTtsMessage = boxTtsMessage?.length
          ? boxTtsMessage
          : ability.tts_message
        if (!isGeneralAssignment) {
          scope.castGroups.forEach((castGroup) => {
            const firstCastIndex = castGroup[0]
            const firstEntry = row.entries[firstCastIndex]
            if (!firstEntry) return
            const castAt = firstEntry.time
            const phaseIndex = firstEntry.phaseIndex
            const phaseStart = phaseStarts[phaseIndex] ?? 0
            const relativeTime = Math.max(castAt - phaseStart, 0)
            const parts = [
              `time:${formatTimeValue(relativeTime)}`,
              notePhaseProperty(phaseIndex),
              `tag:${tokens.join(' ')}`,
              `text:${lineText}`,
            ]
            appendColorsProperty(parts, ability.rgb)
            appendTtsProperty(parts, lineTtsMessage)
            assignmentLines.push({
              at: castAt,
              line: `${parts.join(';')};`,
            })
          })
        }

        const abilitySummary = summaryByAbility.get(
          normalizedEntry.abilityKey,
        ) ?? {
          title: abilityName,
          subtitle:
            !isGeneralAssignment &&
            customAbilityMessage &&
            customAbilityMessage !== defaultAssignmentLabel
              ? customAbilityMessage
              : undefined,
          assignmentGroups,
          scopes: [],
        }
        let summaryScope = abilitySummary.scopes.find(
          (entry) => entry.id === scope.id,
        )
        if (!summaryScope) {
          summaryScope = { id: scope.id, label: scopeLabel, groups: [] }
          abilitySummary.scopes.push(summaryScope)
        }
        const defaultGroupName = i18n.t(
          'dashboard.team.plans.assignments.group-label',
          { count: boxIndex + 1 },
        )
        const groupName =
          isGeneralAssignment && boxMessage
            ? boxMessage
            : boxMessage
            ? `${defaultGroupName} (${boxMessage})`
            : defaultGroupName
        const existingGroup = summaryScope.groups.find(
          (group) => group.name === groupName,
        )
        if (existingGroup) {
          tokens.forEach((token) => {
            if (!existingGroup.tokens.includes(token)) {
              existingGroup.tokens.push(token)
            }
          })
        } else {
          summaryScope.groups.push({ name: groupName, tokens: [...tokens] })
        }
        summaryByAbility.set(normalizedEntry.abilityKey, abilitySummary)
      }
    })
  })

  const timedLines = [
    ...actorCastLines,
    ...generalCastLines,
    ...assignmentLines,
  ]
  const summaryLines = Array.from(summaryByAbility.entries()).flatMap(
    ([, abilitySummary], abilityIndex) => {
      const lines = formatAssignmentSummaryLines(abilitySummary)
      if (abilityIndex < summaryByAbility.size - 1) {
        lines.push('')
      }
      return lines
    },
  )

  return { timedLines, summaryLines }
}

export const buildEventActivityLinkedPlanNote = ({
  activity,
  linkedPlanData,
  allEntries,
  planAbilityDefaults,
}: {
  activity: wowaudit.EventActivity
  linkedPlanData?: EventActivityLinkedPlanData
  allEntries: EventActivityParticipantEntry[]
  planAbilityDefaults?: PlanAbilityDefaults
}) => {
  const encounter = activity.encounter
  const selectedParticipants = linkedPlanData?.selectedParticipants
  const difficultyLabel =
    difficultiesLabels[activity.difficulty] ?? activity.difficulty
  const header = encounter
    ? `EncounterID:${encounter.wclId};Difficulty:${difficultyLabel};Name:${encounter.name}`
    : ''

  const noteChunks: string[] = []
  if (header) {
    noteChunks.push(header)
  }

  const raidPlanParts = (linkedPlanData?.raidPlans ?? []).map((raidPlanData) =>
    buildLinkedRaidPlanNoteParts(raidPlanData, planAbilityDefaults),
  )
  const timedLines = raidPlanParts
    .flatMap((parts) => parts.timedLines)
    .sort((left, right) => {
      if (left.at !== right.at) return left.at - right.at
      return left.line.localeCompare(right.line)
    })
    .map((entry) => entry.line)
  const summaryLines = raidPlanParts.flatMap((parts, index) => {
    if (parts.summaryLines.length === 0) return []
    if (index > 0 && parts.summaryLines[0] !== '') {
      return ['', ...parts.summaryLines]
    }
    return parts.summaryLines
  })

  if (summaryLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...summaryLines)
  }
  if (timedLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...timedLines)
  }

  const inviteLine = buildEventActivityInviteListLine({
    activity,
    allEntries,
    selectedParticipants,
  })
  if (inviteLine.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(inviteLine)
  }

  return noteChunks.join('\n')
}
